/**
 * /docs/permissions — the public permission inventory, in the Carbon type of
 * mockup carbon/01 inside the 04 public frame. Every grant table is a Carbon
 * DataTable (stacked rows on phones, its own horizontal scroll otherwise), the
 * permission groups are a Carbon accordion, and notes are neutral asides.
 * Each data array names its source of truth beside it.
 */
import type { ReactNode } from 'react'
import {
  Accordion,
  ButtonLink,
  CodeSnippet,
  CondensedGrid,
  DataTable,
  IconArrowRight,
  Tile,
  TileHeader,
  type DataColumn,
} from '@/components/carbon'
import { PUBLIC_LINK } from '@/components/PublicLayout'
import { DocList, DocNote, DocsHeader, DocSection, DocsPage, Strong } from './docs-ui'
import {
  guardianExchangePermissions,
  guardianSharePointPermissions,
  identityPermissions,
  intunePermissions,
  monitoringPermissions,
  scubaPimPermissions,
  securityPermissions,
  sharePointSettingsPermissions,
  type Permission,
} from './guardian-permissions'

function Code({ children }: { children: string }) {
  return <CodeSnippet kind="inline" code={children} />
}

const PERMISSION_COLUMNS: DataColumn<Permission>[] = [
  { id: 'permission', header: 'Permission', width: '42%', primary: true, cell: (p) => <Code>{p.permission}</Code> },
  { id: 'purpose', header: 'Purpose', cell: (p) => p.purpose },
]

function PermissionTable({ permissions, label }: { permissions: Permission[]; label: string }) {
  return (
    <DataTable
      aria-label={label}
      columns={PERMISSION_COLUMNS}
      rows={permissions}
      rowId={(p) => p.permission}
      size="md"
    />
  )
}

interface Row3 {
  id: string
  a: ReactNode
  b: ReactNode
  c: ReactNode
}

function threeColumns(a: string, b: string, c: string): DataColumn<Row3>[] {
  return [
    { id: 'a', header: a, primary: true, cell: (r) => r.a },
    { id: 'b', header: b, cell: (r) => r.b },
    { id: 'c', header: c, cell: (r) => r.c },
  ]
}

// Microsoft 365 Tenant Configuration Manager (Microsoft first-party
// service, appId 03b07b79-c5bc-4b5e-9bfa-13acf4a99998).
// Source of truth: Policies/common/tcm_required_app_roles.json
const tcmEntraPermissions: Permission[] = [
  { permission: 'Policy.Read.All', purpose: 'Read Conditional Access, authentication method, and authentication strength policies' },
  { permission: 'Policy.ReadWrite.AuthenticationMethod', purpose: 'Write-capable. Microsoft’s configuration manager only monitors, so nothing uses it today. Policy reads come from Policy.Read.All.' },
  { permission: 'Policy.ReadWrite.ConditionalAccess', purpose: 'Write-capable. Microsoft’s configuration manager only monitors, so nothing uses it today. Policy reads come from Policy.Read.All.' },
  { permission: 'Application.Read.All', purpose: 'Read application registrations for application configuration monitoring' },
  { permission: 'User.Read.All', purpose: 'Resolve user names referenced inside policy definitions' },
  { permission: 'Group.Read.All', purpose: 'Resolve group names referenced inside policy definitions' },
  { permission: 'CrossTenantInformation.ReadBasic.All', purpose: 'Read cross-tenant access policy partner information' },
  { permission: 'AuthenticationContext.Read.All', purpose: 'Read authentication context class references' },
  { permission: 'Organization.Read.All', purpose: 'Read authorization policy and tenant-level organization settings' },
  { permission: 'RoleManagement.Read.Directory', purpose: 'Read directory role and PIM assignments referenced in policy exports' },
  { permission: 'AdministrativeUnit.Read.All', purpose: 'Read administrative unit configurations' },
  { permission: 'Agreement.Read.All', purpose: 'Read terms of use agreements' },
  { permission: 'CustomSecAttributeDefinition.Read.All', purpose: 'Read custom security attribute definitions' },
]

const tcmWorkloadPermissions: Permission[] = [
  { permission: 'Mail.ReadBasic.All', purpose: 'Read mail flow configuration through Graph mail endpoints. The permission excludes message bodies and attachments, and Guardian holds no credentials for this service principal.' },
  { permission: 'InformationProtectionPolicy.Read.All', purpose: 'Read data loss prevention and retention policy configuration' },
  { permission: 'DeviceManagementConfiguration.Read.All', purpose: 'Read Intune device compliance and configuration policies' },
  { permission: 'DeviceManagementManagedDevices.Read.All', purpose: 'Read managed device configuration' },
  { permission: 'DeviceManagementApps.Read.All', purpose: 'Read Intune app, app-protection, and app-configuration policies' },
  { permission: 'DeviceManagementServiceConfig.Read.All', purpose: 'Read Intune service configuration (enrollment restrictions, device categories)' },
  { permission: 'DeviceManagementRBAC.Read.All', purpose: 'Read Intune role and scope assignments' },
  { permission: 'Team.ReadBasic.All', purpose: 'Read basic Teams information' },
  { permission: 'TeamSettings.Read.All', purpose: 'Read Teams client and guest configuration' },
  { permission: 'Channel.ReadBasic.All', purpose: 'Read Teams channel policy configuration' },
  { permission: 'Calls.AccessMedia.All', purpose: 'Required by Microsoft to export Teams calling policies (Guardian never accesses call media or audio)' },
  { permission: 'OnlineMeetings.Read.All', purpose: 'Read Teams meeting policies' },
  { permission: 'CallRecords.Read.All', purpose: 'Read call record metadata for calling policy configuration evidence' },
]

/** The Graph read grants the capability table cites, counted from the lists that render below. */
export const TCM_GRAPH_PERMISSION_COUNT = tcmEntraPermissions.length + tcmWorkloadPermissions.length

const CAPABILITY_ROWS: Row3[] = [
  {
    id: 'drift-tcm',
    a: 'Drift monitoring: Entra ID, Exchange, Intune, Teams, Defender',
    b: 'M365 Tenant Configuration Manager',
    c: `${TCM_GRAPH_PERMISSION_COUNT} Graph permissions, Global Reader + Security Reader, Exchange configuration roles`,
  },
  {
    id: 'drift-monitors',
    a: 'Drift monitoring: monitors, snapshots and drift results',
    b: 'iuvo Guardian',
    c: <Code>ConfigurationMonitoring.ReadWrite.All</Code>,
  },
  {
    id: 'drift-spo',
    a: 'Drift monitoring: SharePoint & OneDrive',
    b: 'iuvo Guardian',
    c: <><Code>Sites.FullControl.All</Code> (SharePoint Online API), <Code>SharePointTenantSettings.Read.All</Code>, <Code>Domain.Read.All</Code></>,
  },
  {
    id: 'bpa',
    a: 'Best Practice checks & Secure Score',
    b: 'iuvo Guardian',
    c: <>Policy, role and Intune Graph reads, <Code>SecurityEvents.Read.All</Code>, <Code>AttackSimulation.Read.All</Code>, Security Reader role</>,
  },
  {
    id: 'users',
    a: 'User analytics (licenses, activity, MFA coverage)',
    b: 'iuvo Guardian',
    c: (
      <>
        <Code>User.Read.All</Code>, <Code>AuditLog.Read.All</Code>, <Code>Organization.Read.All</Code>,{' '}
        <Code>Group.Read.All</Code>, Exchange View-Only Recipients role
      </>
    ),
  },
  {
    id: 'apps',
    a: 'Application insights (enterprise app risk & consent monitoring)',
    b: 'iuvo Guardian',
    c: <><Code>Application.Read.All</Code>, <Code>Directory.Read.All</Code>, <Code>AuditLog.Read.All</Code></>,
  },
  {
    id: 'scuba',
    a: 'CISA ScubaGear assessment (on request)',
    b: 'iuvo Guardian',
    c: <>Entra policy and role reads, <Code>Exchange.ManageAsApp</Code>, <Code>Sites.FullControl.All</Code> (SharePoint Online API)</>,
  },
]

const ROLE_ROWS: Row3[] = [
  { id: 'global-reader', a: <Code>Global Reader</Code>, b: 'Both service principals', c: 'Read-only access to administrative features across workloads' },
  { id: 'security-reader', a: <Code>Security Reader</Code>, b: 'Both service principals', c: 'Read-only access to security, compliance, and data protection features' },
  { id: 'view-only-recipients', a: <Code>View-Only Recipients</Code>, b: 'iuvo Guardian', c: 'Read-only Exchange recipient data for user analytics' },
  { id: 'view-only-configuration', a: <Code>View-Only Configuration</Code>, b: 'M365 Tenant Configuration Manager', c: 'Read-only Exchange organization configuration for drift export' },
  { id: 'email-address-policies', a: <Code>E-Mail Address Policies</Code>, b: 'M365 Tenant Configuration Manager', c: 'Email address policy export (narrow role; no read-only equivalent exists)' },
  { id: 'compliance-administrator', a: <Code>Compliance Administrator</Code>, b: 'M365 Tenant Configuration Manager (opt-in only)', c: 'Purview compliance configuration export (write-capable, off by default)' },
]

const BEST_PRACTICES = [
  {
    title: 'Principle of least privilege',
    description:
      'The Guardian app requests only the permissions its features use, and this page lists each one with its purpose. Every write-capable grant is marked, with the reason it is there.',
  },
  { title: 'Certificate-based authentication', description: 'More secure than client secrets, and cannot be accidentally shared.' },
  { title: 'No user impersonation', description: 'Uses application permissions, and cannot act as any user.' },
]

export default function PermissionsGuide() {
  // ---------------------------------------------------------------
  // Microsoft 365 Tenant Configuration Manager (Microsoft first-party
  // service, appId 03b07b79-c5bc-4b5e-9bfa-13acf4a99998).
  // Source of truth: Policies/common/tcm_required_app_roles.json
  // ---------------------------------------------------------------
  const tcmExchangeAndRoles = [
    { permission: 'Exchange.ManageAsApp', purpose: 'API permission to authenticate to Exchange Online as an application for configuration export' },
    { permission: 'Global Reader (directory role)', purpose: 'Tenant-wide read access covering Teams, Exchange, and Defender configuration exports that are role-gated rather than permission-gated' },
    { permission: 'Security Reader (directory role)', purpose: 'Read access to Defender and security configuration' },
    { permission: 'View-Only Configuration (Exchange role)', purpose: 'Read-only Exchange role covering mail flow, anti-phishing, anti-spam, and organization configuration export' },
    { permission: 'E-Mail Address Policies (Exchange role)', purpose: 'Enables email address policy export. This narrow role has no read-only equivalent; it covers only the email-address-policy family of settings' },
  ]

  return (
    <DocsPage crumbs={[{ label: 'Documentation', to: '/docs' }, { label: 'Permissions & security' }]}>
      <DocsHeader
        title="Review every permission Guardian holds."
        lede="A complete inventory of every permission Guardian requires: which service principal holds it, what it is for, and the measures that protect your environment."
      />

      {/* Service principals */}
      <DocSection title="The two service principals in your tenant" id="service-principals">
        <p className="m-0">
          Guardian&rsquo;s access to your tenant runs through two service principals. Every permission on this page
          belongs to one of them, and you can audit both independently in Microsoft Entra ID under{' '}
          <Strong>Enterprise applications</Strong>.
        </p>
        <CondensedGrid columns={{ base: 1, 'c-md': 2 }}>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="iuvo Guardian" level={3} />
            <p className="m-0 text-helper text-text-helper">
              App ID: <Code>10ab017c-a1da-422f-be0e-fda46c03bec9</Code>
            </p>
            <p className="m-0 text-body text-text-secondary">
              iuvo&rsquo;s collector application. Reads your Microsoft 365 configuration for SharePoint and OneDrive
              drift monitoring, the Best Practices Analyzer, user analytics, and application insights. Authenticates
              with a certificate, never a client secret.
            </p>
          </Tile>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="Microsoft 365 Tenant Configuration Manager" level={3} />
            <p className="m-0 text-helper text-text-helper">
              App ID: <Code>03b07b79-c5bc-4b5e-9bfa-13acf4a99998</Code>
            </p>
            <p className="m-0 text-body text-text-secondary">
              A <Strong>Microsoft first-party service</Strong> (the same app ID in every tenant) that performs
              configuration exports inside your tenant. Guardian orchestrates it to monitor drift across Entra ID,
              Exchange Online, Intune, Teams, and Microsoft Defender. The grants below let its exports read your
              configuration.
            </p>
          </Tile>
        </CondensedGrid>
      </DocSection>

      {/* Capability map */}
      <DocSection title="What each Guardian capability uses" id="capabilities">
        <DataTable
          aria-label="What each Guardian capability uses"
          columns={threeColumns('Capability', 'Service principal', 'Key grants')}
          rows={CAPABILITY_ROWS}
          rowId={(r) => r.id}
          size="md"
        />
      </DocSection>

      {/* Security architecture */}
      <DocSection title="Security architecture" id="security-architecture">
        <CondensedGrid columns={{ base: 1, 'c-md': 2 }}>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="Certificate-based auth" level={3} />
            <DocList
              items={[
                <><Strong>Certificate, not a client secret.</Strong> Guardian signs in to your tenant as its own app with an iuvo-held certificate stored in Azure Key Vault.</>,
                <><Strong>Audit trail.</Strong> Usage is logged in sign-in logs.</>,
              ]}
            />
          </Tile>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="Read-only directory roles" level={3} />
            <DocList
              items={[
                <><Strong>Global Reader.</Strong> Read-only access to administrative features (both service principals).</>,
                <><Strong>Security Reader.</Strong> Read-only access to security and compliance features (both service principals).</>,
                <><Strong>Exchange roles.</Strong> View-Only Recipients and View-Only Configuration are read-only by design.</>,
              ]}
            />
          </Tile>
        </CondensedGrid>
      </DocSection>

      {/* Guardian app: Microsoft Graph permissions */}
      <DocSection title="iuvo Guardian: Microsoft Graph permissions" id="guardian-graph">
        <p className="m-0 text-helper text-text-helper">
          Service principal: iuvo Guardian (<Code>10ab017c-a1da-422f-be0e-fda46c03bec9</Code>)
        </p>
        <p className="m-0">
          All permissions below are <Strong>Application permissions</Strong> (not delegated), meaning they operate
          without a signed-in user context. This is appropriate for automated monitoring services.
        </p>
        {/* Temporary: remove at permission-trim SOP §8, with the app-registration change. */}
        <DocNote title="The consent screen doesn’t match this page yet.">
          <p className="m-0">
            Guardian&rsquo;s app registration still asks for older permissions from before Guardian used
            Microsoft&rsquo;s Tenant Configuration Management, and doesn&rsquo;t ask for{' '}
            <Code>AttackSimulation.Read.All</Code> yet. iuvo is updating it: the older permissions come off and{' '}
            <Code>AttackSimulation.Read.All</Code> is added. Until then, the consent screen lists permissions this page
            doesn&rsquo;t, and the attack simulation check shows no data. This page lists what Guardian uses after the
            update.
          </p>
        </DocNote>
        <Accordion
          defaultOpen={['identity']}
          items={[
            { id: 'identity', title: 'Identity & directory permissions (read-only)', content: <PermissionTable label="Identity and directory permissions" permissions={identityPermissions} /> },
            { id: 'security', title: 'Security, policy & role permissions (read-only)', content: <PermissionTable label="Security, policy and role permissions" permissions={securityPermissions} /> },
            { id: 'intune', title: 'Intune permissions (read-only)', content: <PermissionTable label="Intune permissions" permissions={intunePermissions} /> },
            { id: 'sharepoint-settings', title: 'SharePoint settings (read-only)', content: <PermissionTable label="SharePoint settings permissions" permissions={sharePointSettingsPermissions} /> },
            { id: 'monitoring', title: 'Drift monitoring setup (write-capable)', content: <PermissionTable label="Drift monitoring setup permissions" permissions={monitoringPermissions} /> },
          ]}
        />
        <DocNote title="One Graph permission can write.">
          <p className="m-0">
            <Code>ConfigurationMonitoring.ReadWrite.All</Code> lets Guardian create and maintain its own monitors and
            snapshots in the Microsoft 365 Tenant Configuration Manager. It does not change your tenant settings: when
            you accept a detected change, Guardian updates its monitor&rsquo;s baseline, not your live policy.
          </p>
        </DocNote>
        <DocNote title="Three more appear only if iuvo turns on ScubaGear’s PIM for Groups checks.">
          <p className="m-0">
            CISA&rsquo;s ScubaGear reads Privileged Identity Management for Groups. If iuvo enables those checks, the
            consent screen also lists these read permissions:
          </p>
          <PermissionTable label="Optional ScubaGear permissions" permissions={scubaPimPermissions} />
        </DocNote>
      </DocSection>

      {/* Guardian app: SharePoint Online */}
      <DocSection title="iuvo Guardian: SharePoint Online access" id="guardian-sharepoint">
        <p className="m-0 text-helper text-text-helper">
          Service principal: iuvo Guardian (<Code>10ab017c-a1da-422f-be0e-fda46c03bec9</Code>), on the SharePoint
          Online API rather than Microsoft Graph
        </p>
        <PermissionTable label="iuvo Guardian SharePoint Online access" permissions={guardianSharePointPermissions} />
        <DocNote title="Why a full-control permission reads your SharePoint settings">
          <p className="m-0">
            Tenant-level SharePoint and OneDrive settings come from SharePoint&rsquo;s admin APIs, and Microsoft offers
            no read-only application permission for them. <Code>Sites.FullControl.All</Code> is write-capable. Guardian
            uses it to read your configuration and does not use the write side.
          </p>
        </DocNote>
      </DocSection>

      {/* Guardian app: Exchange Online */}
      <DocSection title="iuvo Guardian: Exchange Online access" id="guardian-exchange">
        <p className="m-0 text-helper text-text-helper">
          Service principal: iuvo Guardian (<Code>10ab017c-a1da-422f-be0e-fda46c03bec9</Code>)
        </p>
        <PermissionTable label="iuvo Guardian Exchange Online access" permissions={guardianExchangePermissions} />
        <DocNote title="Why Guardian needs Exchange recipient access">
          <p className="m-0">
            User analytics needs to tell shared mailboxes apart from licensed user mailboxes. Shared mailboxes have no
            sign-in activity, so without this distinction, inactive-user and license-waste reporting would be wrong. The{' '}
            <Strong>View-Only Recipients</Strong> Exchange role grants exactly that read access and nothing more. It is
            assigned by the setup script via Exchange Online PowerShell because Microsoft does not expose Exchange role
            assignment through the consent screen or the Azure portal.
          </p>
        </DocNote>
      </DocSection>

      {/* Configuration manager grants */}
      <DocSection title="Microsoft 365 Tenant Configuration Manager: grants" id="configuration-manager">
        <p className="m-0 text-helper text-text-helper">
          Service principal: M365 Tenant Configuration Manager (<Code>03b07b79-c5bc-4b5e-9bfa-13acf4a99998</Code>,
          Microsoft first-party)
        </p>
        <p className="m-0">
          Configuration drift monitoring for Entra ID, Exchange Online, Intune, Teams, and Microsoft Defender runs
          through Microsoft&rsquo;s own configuration management service. Your onboarding grants that service the read
          permissions its exports need. These grants are made to Microsoft&rsquo;s service principal; iuvo holds no
          credentials for it.
        </p>
        <Accordion
          defaultOpen={['entra']}
          items={[
            { id: 'entra', title: 'Entra ID & policy permissions', content: <PermissionTable label="Entra ID and policy permissions" permissions={tcmEntraPermissions} /> },
            { id: 'workloads', title: 'Exchange, compliance, Intune & Teams permissions', content: <PermissionTable label="Exchange, compliance, Intune and Teams permissions" permissions={tcmWorkloadPermissions} /> },
            { id: 'exchange-roles', title: 'Exchange Online access & directory roles', content: <PermissionTable label="Exchange Online access and directory roles" permissions={tcmExchangeAndRoles} /> },
          ]}
        />
        <DocNote title="Optional Purview add-on">
          <p className="m-0">
            Monitoring Purview compliance configuration (eDiscovery, retention, DLP compliance settings) requires the{' '}
            <Strong>Compliance Administrator</Strong> role on the configuration manager service principal. Because that
            role is write-capable, it is <Strong>off by default</Strong> and granted only if you explicitly opt in during
            onboarding.
          </p>
        </DocNote>
      </DocSection>

      {/* Directory roles summary */}
      <DocSection title="Directory & Exchange roles at a glance" id="roles">
        <p className="m-0">
          In addition to API permissions, the following roles are assigned. All are <Strong>read-only</Strong> unless
          noted:
        </p>
        <DataTable
          aria-label="Directory and Exchange roles"
          columns={threeColumns('Role', 'Assigned to', 'Purpose')}
          rows={ROLE_ROWS}
          rowId={(r) => r.id}
          size="md"
        />
      </DocSection>

      {/* Security best practices */}
      <DocSection title="Security best practices implemented" id="best-practices">
        <CondensedGrid columns={{ base: 1, 'c-md': 2 }}>
          {BEST_PRACTICES.map((item) => (
            <Tile key={item.title} className="flex min-w-0 flex-col gap-2">
              <TileHeader title={item.title} level={3} />
              <p className="m-0 text-body text-text-secondary">{item.description}</p>
            </Tile>
          ))}
        </CondensedGrid>
      </DocSection>

      {/* Monitoring activity */}
      <DocSection title="Monitoring Guardian’s activity" id="monitoring-activity">
        <p className="m-0">You can monitor all Guardian activity in your tenant:</p>
        <CondensedGrid columns={{ base: 1, 'c-md': 2 }}>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="Microsoft Entra ID sign-in logs" level={3} />
            <DocList
              ordered
              items={[
                <>Go to <Strong>Azure Portal</Strong> &rarr; <Strong>Microsoft Entra ID</Strong> &rarr; <Strong>Sign-in logs</Strong></>,
                <>Filter by <Strong>Application</Strong>: &ldquo;Guardian by iuvo&rdquo; or &ldquo;M365 Tenant Configuration Manager&rdquo;</>,
                'Review all authentication events and accessed resources',
              ]}
            />
          </Tile>
          <Tile className="flex min-w-0 flex-col gap-3">
            <TileHeader title="Microsoft Entra ID audit logs" level={3} />
            <DocList
              ordered
              items={[
                <>Go to <Strong>Azure Portal</Strong> &rarr; <Strong>Microsoft Entra ID</Strong> &rarr; <Strong>Audit logs</Strong></>,
                <>Filter by <Strong>Initiated by (actor)</Strong>: Service Principal</>,
                'Search for either App ID listed at the top of this page',
              ]}
            />
          </Tile>
        </CondensedGrid>
      </DocSection>

      {/* Revoking access */}
      <DocSection title="Revoking access" id="revoking-access">
        <p className="m-0">If you need to revoke Guardian&rsquo;s access to your tenant:</p>
        <Accordion
          defaultOpen={['revoke-consent']}
          items={[
            {
              id: 'revoke-consent',
              title: 'Option 1: Revoke admin consent (recommended)',
              content: (
                <div className="flex flex-col gap-3">
                  <DocList
                    ordered
                    items={[
                      <>Go to <Strong>Azure Portal</Strong> &rarr; <Strong>Microsoft Entra ID</Strong> &rarr; <Strong>Enterprise applications</Strong></>,
                      <>Search for <Strong>&ldquo;Guardian by iuvo&rdquo;</Strong></>,
                      <>Go to <Strong>Permissions</Strong></>,
                      <>Click <Strong>Review permissions</Strong> &rarr; select <Strong>&ldquo;This application has more permissions than I want&rdquo;</Strong></>,
                      'Follow the steps to revoke admin consent',
                    ]}
                  />
                  <p className="m-0 text-helper text-text-helper">
                    This immediately revokes Guardian&rsquo;s API permissions while keeping the enterprise app in your
                    tenant for easy re-authorization later.
                  </p>
                </div>
              ),
            },
            {
              id: 'remove-app',
              title: 'Option 2: Remove the enterprise app',
              content: (
                <div className="flex flex-col gap-3">
                  <DocList
                    ordered
                    items={[
                      <>Go to <Strong>Azure Portal</Strong> &rarr; <Strong>Microsoft Entra ID</Strong> &rarr; <Strong>Enterprise applications</Strong></>,
                      <>Search for <Strong>&ldquo;Guardian by iuvo&rdquo;</Strong></>,
                      <>Click <Strong>Delete</Strong></>,
                    ]}
                  />
                  <p className="m-0 text-helper text-text-helper">
                    This fully removes Guardian&rsquo;s service principal, all granted permissions, and role assignments
                    from your tenant.
                  </p>
                </div>
              ),
            },
            {
              id: 'configuration-manager-grants',
              title: 'What about the configuration manager grants?',
              content: (
                <p className="m-0">
                  The Microsoft 365 Tenant Configuration Manager is Microsoft&rsquo;s own service principal, so it is
                  not deleted. Instead, Guardian&rsquo;s offboarding removes the role assignments and permission grants
                  that onboarding created. Contact support and we will run the teardown with you, or your admin can
                  remove the grants directly in Entra ID and Exchange Online.
                </p>
              ),
            },
          ]}
        />
      </DocSection>

      {/* FAQ */}
      <DocSection title="Frequently asked questions" id="faq">
        <Accordion
          items={[
            {
              id: 'modify',
              title: 'Can Guardian modify my tenant configuration?',
              content: (
                <p className="m-0">
                  <Strong>Monitoring does not change your tenant settings.</Strong> Guardian reads your configuration to
                  detect drift. Two of its grants are write-capable. <Code>ConfigurationMonitoring.ReadWrite.All</Code>{' '}
                  maintains Guardian&rsquo;s own monitors in the Microsoft 365 Tenant Configuration Manager: when you
                  accept a detected change, Guardian updates its monitor&rsquo;s baseline, not your live policies.{' '}
                  <Code>Sites.FullControl.All</Code> on the SharePoint Online API is the only permission Microsoft offers
                  for exporting SharePoint and OneDrive settings, and Guardian does not use its write side. You can review
                  what each service principal does in your tenant&rsquo;s sign-in and audit logs.
                </p>
              ),
            },
            {
              id: 'readwrite',
              title: 'Which grants can write, and why?',
              content: (
                <div className="flex flex-col gap-3">
                  <p className="m-0">
                    On the iuvo Guardian app, two. <Code>ConfigurationMonitoring.ReadWrite.All</Code> maintains
                    Guardian&rsquo;s own monitors and snapshots. <Code>Sites.FullControl.All</Code> on the SharePoint
                    Online API reads SharePoint and OneDrive settings that no narrower permission can export, and
                    Guardian does not use its write side.
                  </p>
                  <p className="m-0">
                    On the configuration manager, <Code>Policy.ReadWrite.ConditionalAccess</Code> and{' '}
                    <Code>Policy.ReadWrite.AuthenticationMethod</Code> are write-capable. The service only monitors, so
                    nothing uses them today, and iuvo holds no credentials for it. The E-Mail Address Policies Exchange role and the opt-in Compliance Administrator
                    role are write-capable for the reasons given in their rows above.
                  </p>
                </div>
              ),
            },
            {
              id: 'controls',
              title: 'Who controls the Microsoft 365 Tenant Configuration Manager?',
              content: (
                <p className="m-0">
                  It is a Microsoft first-party service; the same app ID exists in every Microsoft 365 tenant. iuvo holds
                  no credentials for it. Guardian instructs the service to take configuration snapshots through
                  Microsoft&rsquo;s APIs, and the service performs the export inside your tenant using the roles you
                  granted at onboarding. Removing those grants stops the exports immediately.
                </p>
              ),
            },
            {
              id: 'collect',
              title: 'What data does Guardian collect?',
              content: (
                <div className="flex flex-col gap-3">
                  <p className="m-0">
                    Guardian reads your Microsoft 365 configuration settings (policies, rules, settings) to detect drift
                    from your approved baseline, plus directory metadata for user and application analytics (user names,
                    license assignments, sign-in activity, MFA registration status, mailbox types, and enterprise app
                    consent grants). It does <Strong>not</Strong> collect:
                  </p>
                  <DocList items={['Email content', 'File contents', 'Chat messages, call audio, or meeting content', 'Passwords or credentials']} />
                </div>
              ),
            },
            {
              id: 'outside',
              title: 'Is my data sent outside my tenant?',
              content: (
                <div className="flex flex-col gap-3">
                  <p className="m-0">
                    Yes, <Strong>configuration data is stored externally</Strong> in iuvo&rsquo;s secure Azure
                    infrastructure to enable drift detection, baseline management, and reporting. This includes:
                  </p>
                  <DocList
                    items={[
                      'Policy settings and configurations',
                      'Security rules and compliance settings',
                      'Tenant configuration metadata',
                      'Directory metadata used for analytics (user names, license and activity data, app consent grants)',
                    ]}
                  />
                  <p className="m-0">
                    <Strong>What is not collected:</Strong> Email content, file contents, chat or call content, or user
                    passwords.
                  </p>
                  <p className="m-0">
                    All external storage is encrypted at rest and in transit, and access is restricted to authorized
                    Guardian systems.
                  </p>
                </div>
              ),
            },
            {
              id: 'how-often',
              title: 'How often does Guardian access my tenant?',
              content: (
                <p className="m-0">
                  Guardian performs scheduled scans (configurable frequency) and on-demand scans when requested. Each scan
                  reads your current configuration to compare against your baseline.
                </p>
              ),
            },
          ]}
        />
      </DocSection>

      {/* Contact */}
      <DocSection title="Contact & support" id="contact">
        <p className="m-0">
          For questions about Guardian&rsquo;s permissions or security, email{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
          .
        </p>
      </DocSection>

      {/* Neighbouring pages */}
      <nav aria-label="Documentation pages" className="mt-12 flex max-w-[56rem] flex-wrap justify-between gap-4 border-t border-line-subtle pt-6">
        <ButtonLink to="/docs/onboarding" kind="ghost">
          Onboarding guide
        </ButtonLink>
        <ButtonLink to="/docs" kind="ghost" icon={<IconArrowRight />}>
          Documentation
        </ButtonLink>
      </nav>
    </DocsPage>
  )
}
